import api from "@/api/v1-jwt/config/api";
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { createErrorHandler } from "@/utils/errorHandler";
import paymentMethodQueryKeys from "./query-keys";
import { z } from "zod";

const ResponseSchema = z.object({
  data: z.object({
    disconnected: z.boolean(),
    error: z.string(),
    message: z.string(),
  }),
  response_message: z.string(),
  response_code: z.number(),
});

type Response = z.infer<typeof ResponseSchema>;

type DisconnectPaymentMethodParams = {
  userId: string;
  type: string;
  paymentMethodId: string;
};

const disconnectPaymentMethod = async ({
  type,
  paymentMethodId,
}: DisconnectPaymentMethodParams): Promise<Response> => {
  const { data } = await api.delete(
    `/platform/payment_methods/${type.toLowerCase()}/${paymentMethodId}`
  );
  return ResponseSchema.parse(data);
};

type DisconnectPaymentMethodMutationOptions = UseMutationOptions<
  Response,
  Error,
  DisconnectPaymentMethodParams
>;

export const useDisconnectPaymentMethodOptions = (
  options?: DisconnectPaymentMethodMutationOptions
): DisconnectPaymentMethodMutationOptions => {
  const queryClient = useQueryClient();
  const onSettled = options?.onSettled;
  const defaultOptions: DisconnectPaymentMethodMutationOptions = {
    mutationFn: disconnectPaymentMethod,
    onSettled: (...params) => {
      queryClient.invalidateQueries(paymentMethodQueryKeys.all);
      if (onSettled) {
        onSettled(...params);
      }
    },
    onError: createErrorHandler(),
  };
  return { ...defaultOptions, ...options };
};

export const useDisconnectPaymentMethod = (
  options?: DisconnectPaymentMethodMutationOptions
) => {
  const mutationOptions = useDisconnectPaymentMethodOptions(options);
  return useMutation<Response, Error, DisconnectPaymentMethodParams>(
    mutationOptions
  );
};

export default useDisconnectPaymentMethod;
